import { KomboIntegration, privateApi } from 'services';
import {
    IntegrationJob,
    KomboConnect,
    KomboPassthroughSettings,
    SupermetricsLoginLink,
    UpdateKomboPassthroughSettingsPayload,
} from './types';
import { v4 as uuid } from 'uuid';

const createLink = (businessAccountId: string, integrationToolId: string): Promise<KomboConnect> =>
    privateApi.post<KomboConnect>(`business-accounts/${businessAccountId}/kombo/connect/create-link`, {
        integrationTool: integrationToolId,
    });

const reconnect = (businessAccountId: string, integrationToolId: string): Promise<KomboConnect> =>
    privateApi.post<KomboConnect>(
        `business-accounts/${businessAccountId}/kombo/integration/${integrationToolId}/reconnect`,
    );

const createSupermetricsLoginLink = (
    businessAccountId: string,
    integrationToolId: string,
): Promise<SupermetricsLoginLink> =>
    privateApi.post<SupermetricsLoginLink>(`business-accounts/${businessAccountId}/supermetrics/generate-login-link`, {
        dataSource: integrationToolId,
    });

const verifyLoginLink = (businessAccountId: string, externalId: string, redirectVerifier: string): Promise<void> =>
    privateApi.post(
        `business-accounts/${businessAccountId}/supermetrics/connections/from-login-link`,
        {
            uuid: uuid(),
            externalId,
            redirectVerifier,
        },
        {},
        true,
    );

const activateIntegration = (businessAccountId: string, token: string): Promise<void> =>
    privateApi.post<void>(`business-accounts/${businessAccountId}/kombo/connect/activate-integration`, {
        token,
    });

const getIntegrations = (businessAccountId: string): Promise<KomboIntegration[]> =>
    privateApi.get<KomboIntegration[]>(`business-accounts/${businessAccountId}/kombo/integrations`);

const searchIntegrationJobs = (
    businessAccountId: string,
    integrationId: string,
    searchQuery: string,
): Promise<IntegrationJob[]> =>
    privateApi.get<IntegrationJob[]>(
        `business-accounts/${businessAccountId}/kombo/integration/${integrationId}/jobs?query=${searchQuery}`,
    );

const syncSettings = (
    integrationId: string,
    syncKomboCandidatesWithTalentsEnabled: boolean,
    syncKomboCandidatesUpdatedWithinMonths: number,
): Promise<void> =>
    privateApi.put<void>(`kombo-integrations/${integrationId}/sync-settings`, {
        syncKomboCandidatesWithTalentsEnabled,
        syncKomboCandidatesUpdatedWithinMonths,
    });

const enableIntegration = (integrationId: string): Promise<void> =>
    privateApi.post<void>(`kombo-integrations/${integrationId}/enable`);

const disableIntegration = (integrationId: string): Promise<void> =>
    privateApi.post<void>(`kombo-integrations/${integrationId}/disable`);
const getPassthroughSettings = (integrationId: string): Promise<KomboPassthroughSettings> =>
    privateApi.get<KomboPassthroughSettings>(`kombo-integrations/${integrationId}/passthrough-settings`);

const updatePassthroughSettings = (
    integrationId: string,
    payload: UpdateKomboPassthroughSettingsPayload,
): Promise<void> =>
    privateApi.put<void>(`kombo-integrations/${integrationId}/passthrough-settings`, payload, undefined, true);

export const KomboGateway = {
    createLink,
    reconnect,
    createSupermetricsLoginLink,
    verifyLoginLink,
    activateIntegration,
    getIntegrations,
    searchIntegrationJobs,
    syncSettings,
    enableIntegration,
    disableIntegration,
    getPassthroughSettings,
    updatePassthroughSettings,
};
